import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePayment } from '@/contexts/PaymentContext';
import { BillingInterval } from '@/types/payment';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { PRICING_PLANS } from '@/services/paymentService';
import Counter from '@/components/ui/Counter';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const { isMobile } = useIsMobile();
  const { pricingPlans, createCheckoutSession, contactSales, isOpeningCheckout, isLoadingPlans, openBillingPortal, subscription } = usePayment();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  
  // SSR-safe: Only use scroll hooks after mount (client-side only)
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
    layoutEffect: false // Use regular effect, not layout effect for SSR safety
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [1, 1, 1, 0]);
  
  // SSR-safe fallback values
  const scrollY = isMounted ? y : "0%";
  const scrollOpacity = isMounted ? opacity : 1;

  // Section refs
  const headerRef = useRef(null);
  const cardsRef = useRef(null);
  const bannerRef = useRef(null);
  const faqRef = useRef(null);

  // InView hooks
  const headerInView = useInView(headerRef, { amount: 0.3, once: true });
  const cardsInView = useInView(cardsRef, { amount: 0.1, once: true });
  const bannerInView = useInView(bannerRef, { amount: 0.2, once: true });
  const faqInView = useInView(faqRef, { amount: 0.2, once: true });

  // SSR-safe: Use static fallback if pricingPlans is empty (during SSR)
  // This ensures the page renders correctly during prerender
  const plansToUse = pricingPlans.length > 0 ? pricingPlans : PRICING_PLANS;
  const plans = plansToUse.map(plan => ({
    ...plan,
    // Override Enterprise pricing to show "Custom"
    ...(plan.id === 'enterprise' && {
      price: { monthly: 'Custom', annual: 'Custom' }
    })
  }));

  const handlePaidPlanClick = (planId: string, billingInterval: BillingInterval) => {
    if (!user) {
      // User not authenticated, redirect to auth with plan selection state
      navigate('/auth', { 
        state: { 
          from: { pathname: '/pricing' },
          selectedPlan: { planId, billingInterval }
        } 
      });
    } else {
      // User is authenticated, proceed with checkout
      if (subscription) { void openBillingPortal().catch(() => {}); return; }
      void createCheckoutSession(planId, billingInterval).catch(() => {}); // Mutation displays the error.
    }
  };

  // Handle selected plan when user returns authenticated
  useEffect(() => {
    if (user && location.state?.selectedPlan) {
      const { planId, billingInterval } = location.state.selectedPlan;
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true });
      // Trigger checkout with the selected plan
      void createCheckoutSession(planId, billingInterval).catch(() => {});
    }
  }, [user, location.state, createCheckoutSession, navigate]);

  const faqs = [
    {
      question: "Can I switch between plans?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and billing is prorated."
    },
    {
      question: "What happens to my data if I cancel?",
      answer: "You can export all your data anytime. After cancellation, your data remains accessible for 90 days, giving you time to export everything."
    },
    {
      question: "Do you offer academic discounts?",
      answer: "Yes! Students and academic researchers get 50% off all paid plans. Verification through your institution email is required."
    },
    {
      question: "Is my research data secure?",
      answer: "Absolutely. We use enterprise-grade encryption, SOC 2 compliance, and follow strict data protection protocols. Your research data is never shared or used for training AI models without explicit consent."
    },
    {
      question: "Can I use Synkro offline?",
      answer: "Core features work offline, and your changes sync when you reconnect. AI features require an internet connection."
    },
  ];

  const getPrice = (plan: typeof plans[0]) => {
    if (typeof plan.price.monthly === 'string') return plan.price.monthly;
    return isAnnual ? plan.price.annual : plan.price.monthly;
  };

  const getPriceLabel = (plan: typeof plans[0]) => {
    if (typeof plan.price.monthly === 'string') return '';
    return plan.price.monthly === 0 ? '' : isAnnual ? 'month' : '/month';
  };

  return (
    <div ref={containerRef} className="landing-gradient-bg">

      {/* Animated Grid Background */}

      {/* Hero Section */}
      <motion.div
        ref={headerRef}
        style={{ y: scrollY, opacity: scrollOpacity }}
        className={`relative landing-hero ${isMobile ? 'pt-16 pb-4 px-4' : 'pt-32 pb-20 px-8 md:px-16 xl:px-32 2xl:px-40'} overflow-hidden`}
      >
        <div className="max-w-screen-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="mb-8"
          >
            <h1 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-5xl md:text-6xl leading-snug' : 'text-5xl md:text-7xl leading-tight'}`}>
              <span className="text-slate-900 dark:text-white">Choose your</span>
              <br />
              <span className="text-slate-900 dark:text-white">Plan</span>
            </h1>
            {!isMobile && (
              <p className="text-xl text-black dark:text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
                Start free and scale as your research grows. All plans include core features 
                with no setup fees or hidden costs.
              </p>
            )}
            
            {/* Billing Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex items-center justify-center gap-4 mb-4"
            >
              <div className="flex items-center justify-center gap-4 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-full p-2 border border-slate-200/50 dark:border-slate-700/50">
                <span className={`font-medium px-4 py-2 rounded-full transition-colors ${!isAnnual ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'text-black dark:text-slate-400'}`}>Monthly</span>
                <button
                  onClick={() => setIsAnnual(!isAnnual)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${isAnnual ? 'bg-purple-600' : 'bg-slate-200 dark:bg-slate-600'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${isAnnual ? 'translate-x-5' : 'translate-x-1'}`}
                  />
                </button>
                                 <span className={`font-medium px-4 py-2 rounded-full transition-colors ${isAnnual ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'text-black dark:text-slate-400'}`}>Annual</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Pricing Cards */}
      <motion.div
        ref={cardsRef}
        className={`relative z-10 ${isMobile ? 'px-4 py-4' : 'px-8 md:px-16 xl:px-32'}`}
      >
        <div className="max-w-screen-2xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'}`}>
            {plans.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={headerInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.6 }}
                  className="group h-full"
                >
                <div className={`relative h-full ${plan.popular ? 'scale-105' : ''}`}>
                  {plan.popular && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={headerInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: 0.7 + index * 0.1, duration: 0.6 }}
                      className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10"
                    >
                      <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg whitespace-nowrap">
                        Most Popular
                      </span>
                    </motion.div>
                  )}
                  
                  <div className={`h-full bg-white/90 dark:bg-slate-800/90 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-500 hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/20 dark:hover:shadow-purple-400/20 ${plan.popular ? 'ring-2 ring-purple-500/20' : ''} flex flex-col ${isMobile ? 'p-4' : 'p-6'}`}>
                    <div className="text-center mb-4">
                      <h3 className={`font-bold text-slate-900 dark:text-white mb-2 ${isMobile ? 'text-lg' : 'text-xl'}`}>{plan.name}</h3>
                      
                      <div className="mt-4">
                        <div className="flex items-baseline justify-center">
                          <span className={`font-bold text-slate-900 dark:text-white ${isMobile ? 'text-xl' : 'text-2xl'}`}>
                            {typeof getPrice(plan) === 'string' ? (
                              getPrice(plan)
                            ) : (
                              <>
                                <span className="inline-flex items-center">
                                  <span className="text-slate-900 dark:text-white">{new Intl.NumberFormat('en', { style: 'currency', currency: plan.currency || 'usd', currencyDisplay: 'narrowSymbol' }).formatToParts(0).find(part => part.type === 'currency')?.value}</span>
                                  <Counter
                                    value={Number(getPrice(plan))}
                                    fontSize={isMobile ? 20 : 24}
                                    padding={0}
                                    gap={0}
                                    textColor="currentColor"
                                    fontWeight={700}
                                    gradientFrom="transparent"
                                    gradientTo="transparent"
                                  />
                                </span>
                                <span className={`text-slate-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                                  {getPriceLabel(plan) && (isAnnual ? '/' : '')}{getPriceLabel(plan)}
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                        {isAnnual && ['pro', 'teams'].includes(plan.id) && typeof plan.price.annual === 'number' && (
                          <p className="text-xs text-slate-500 mt-1">
                            Billed {new Intl.NumberFormat('en', { style: 'currency', currency: plan.currency || 'usd', maximumFractionDigits: 0 }).format(plan.price.annual * 12)} per year
                          </p>
                        )}
                        {plan.note && (
                          <p className={`text-slate-500 mt-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>{plan.note}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4 flex-grow">
                      {plan.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start gap-2">
                          <div className={`${isMobile ? 'p-0.5' : 'p-0.5'} bg-green-100 dark:bg-green-900/30 rounded-full mt-0.5 flex-shrink-0`}>
                            <Check className={`${isMobile ? 'h-2 w-2' : 'h-2.5 w-2.5'} text-green-600 dark:text-green-400`} />
                          </div>
                          <span className={`text-slate-700 dark:text-slate-300 leading-relaxed ${isMobile ? 'text-xs' : 'text-xs'}`}>
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {plan.id === 'enterprise' ? (
                      <Button
                        className={`w-full relative ${plan.popular ? 'bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white' : 'bg-black/5 dark:bg-white/5 backdrop-blur-md hover:bg-black/10 dark:hover:bg-white/10 border border-black/20 dark:border-white/20 text-black dark:text-white hover:text-black dark:hover:text-white'} drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group ${isMobile ? 'text-xs' : 'text-sm'}`}
                        variant="ghost"
                        size={isMobile ? "sm" : "sm"}
                        onClick={() => { void contactSales('', '', '').catch(() => {}); }}
                      >
                        <span className="relative z-10 font-medium flex items-center">
                          {plan.cta}
                          <ArrowRight className={`ml-2 ${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                        </span>
                      </Button>
                    ) : plan.id === 'free' ? (
                      <Link to="/auth" state={{ from: { pathname: '/pricing' } }} className="block mt-auto">
                        <Button
                          className={`w-full relative ${plan.popular ? 'bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white' : 'bg-black/5 dark:bg-white/5 backdrop-blur-md hover:bg-black/10 dark:hover:bg-white/10 border border-black/20 dark:border-white/20 text-black dark:text-white hover:text-black dark:hover:text-white'} drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group ${isMobile ? 'text-xs' : 'text-sm'}`}
                          variant="ghost"
                          size={isMobile ? "sm" : "sm"}
                        >
                          <span className="relative z-10 font-medium flex items-center">
                            {plan.cta}
                            <ArrowRight className={`ml-2 ${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                          </span>
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        className={`w-full relative ${plan.popular ? 'bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white' : 'bg-black/5 dark:bg-white/5 backdrop-blur-md hover:bg-black/10 dark:hover:bg-white/10 border border-black/20 dark:border-white/20 text-black dark:text-white hover:text-black dark:hover:text-white'} drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group ${isMobile ? 'text-xs' : 'text-sm'}`}
                        variant="ghost"
                        size={isMobile ? "sm" : "sm"}
                        disabled={isOpeningCheckout || isLoadingPlans || pricingPlans.length === 0}
                        onClick={() => handlePaidPlanClick(plan.id, isAnnual ? 'annual' : 'monthly' as BillingInterval)}
                      >
                        <span className="relative z-10 font-medium flex items-center">
                          {plan.cta}
                          <ArrowRight className={`ml-2 ${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Academic Discount Banner */}
      <motion.div
        ref={bannerRef}
        className={`relative z-10 ${isMobile ? 'px-4 py-8' : 'px-8 md:px-16 xl:px-32 py-12'}`}
      >
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={bannerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="relative"
          >
            <div className={`relative bg-gradient-to-r from-green-500 to-blue-600 rounded-xl text-white overflow-hidden ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className={`font-semibold ${isMobile ? 'text-sm' : 'text-base'}`}>
                    50% off for students & researchers
                  </p>
                </div>
                <Link to="/auth" state={{ from: { pathname: '/pricing' } }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative bg-white/20 backdrop-blur-md hover:bg-white/30 border border-white/30 text-white hover:text-white drop-shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-white/20 overflow-hidden group h-8 px-4"
                  >
                    <span className="relative z-10 font-medium flex items-center">
                      Claim
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </span>
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>



      {/* FAQ Section */}
      <motion.div
        ref={faqRef}
        className={`relative z-10 landing-section ${isMobile ? 'px-4 py-16' : 'px-8 md:px-16 xl:px-32 2xl:px-40 py-24'}`}
      >
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className={`text-center ${isMobile ? 'mb-12' : 'mb-16'}`}
          >
            <h2 className={`font-bold text-slate-900 dark:text-white mb-4 ${isMobile ? 'text-3xl' : 'text-4xl'}`}>
              Frequently Asked Questions
            </h2>
            {!isMobile && (
              <p className="text-xl text-black dark:text-slate-300">
                Everything you need to know about Synkro pricing and plans
              </p>
            )}
          </motion.div>

          <div className={`grid ${isMobile ? 'grid-cols-1 gap-6' : 'grid-cols-1 md:grid-cols-2 gap-8'}`}>
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={faqInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group"
              >
                <div className={`bg-white/90 dark:bg-slate-800/90 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 hover:shadow-lg ${isMobile ? 'p-6' : 'p-8'}`}>
                  <h3 className={`font-semibold text-slate-900 dark:text-white mb-4 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {faq.question}
                  </h3>
                  <p className={`text-black dark:text-slate-300 leading-relaxed ${isMobile ? 'text-sm' : 'text-base'}`}>
                    {faq.answer}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Pricing;
